import React from 'react';
import { useNavigate } from 'react-router-dom';
import './dashboard-data-block.css';

/**
 * Универсальный блок данных для Dashboard по макету Figma (nodes 604-54151, 604-54418, 604-54520).
 * Заголовок с числом, кнопка «В раздел» (зелёная), таблица с колонками.
 * Поддержка цветных строк через rowVariants: 'red' (#F33F4E), 'blue' (#5283F4), 'default' (#383838).
 *
 * @param {string} title - Заголовок блока
 * @param {number|string} count - Число рядом с заголовком
 * @param {string} linkTo - URL для кнопки "В раздел"
 * @param {Array<string>} columns - Заголовки колонок таблицы
 * @param {Array<Array<string|number>>} rows - Строки таблицы (массив массивов)
 * @param {Array<string>} rowVariants - Цветовые варианты строк: 'red', 'blue', 'default'
 * @param {boolean} hasTotalRow - Последняя строка = итого
 */
const DashboardDataBlock = ({
  title = '',
  count = '',
  linkTo = '#',
  className = '',
  columns = [],
  rows = [],
  rowVariants = [],
  hasTotalRow = false,
  sortableColumns = [],
  sortState = null,
  onSortChange = null,
}) => {
  const navigate = useNavigate();
  const isModifiedClick = (e) => e.metaKey || e.altKey || e.ctrlKey || e.shiftKey;

  const handleLinkClick = (e) => {
    if (e.button !== 0 || isModifiedClick(e)) {
      return;
    }
    e.preventDefault();
    navigate(linkTo);
  };

  const handleLinkKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      navigate(linkTo);
    }
  };

  const formatNumber = (val) => {
    if (val === null || val === undefined || val === '') return '—';
    const num = Number(val);
    if (Number.isNaN(num)) return val;
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  const getRowClassName = (rowIdx) => {
    const isTotal = hasTotalRow && rowIdx === rows.length - 1;
    const variant = rowVariants[rowIdx] || '';
    const classes = ['ddb__row'];
    if (isTotal) classes.push('ddb__row--total');
    if (variant === 'red') classes.push('ddb__row--red');
    if (variant === 'blue') classes.push('ddb__row--blue');
    if (variant === 'default') classes.push('ddb__row--default');
    return classes.join(' ');
  };

  const handleSort = (columnIndex) => {
    if (!onSortChange) return;
    onSortChange(columnIndex);
  };

  const getSortIndicator = (columnIndex) => {
    if (!sortState || sortState.columnIndex !== columnIndex) return '↕';
    return sortState.direction === 'asc' ? '↑' : '↓';
  };

  return (
    <section className={`ddb ${className}`.trim()} aria-label={`Блок: ${title}`}>
      <div className="ddb__header">
        <h2 className="ddb__title">
          {title}
          {count !== '' && count !== null && count !== undefined && (
            <span className="ddb__count">{formatNumber(count)}</span>
          )}
        </h2>
        <a
          href={linkTo}
          className="ddb__link"
          onClick={handleLinkClick}
          onKeyDown={handleLinkKeyDown}
          tabIndex={0}
          aria-label={`Перейти в раздел ${title}`}
        >
          В раздел
          <svg
            className="ddb__link-icon"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M4.5 3L7.5 6L4.5 9"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </a>
      </div>

      <div className="ddb__table-wrap">
        <table className="ddb__table">
          <thead>
            <tr className="ddb__row ddb__row--header">
              {columns.map((col, i) => (
                <th key={i} className="ddb__cell ddb__cell--header">
                  {sortableColumns.includes(i) ? (
                    <button
                      type="button"
                      className="ddb__sort-btn"
                      onClick={() => handleSort(i)}
                      aria-label={`Сортировать по столбцу ${col}`}
                    >
                      {col}
                      <span className="ddb__sort-indicator">{getSortIndicator(i)}</span>
                    </button>
                  ) : col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIdx) => {
              const isTotal = hasTotalRow && rowIdx === rows.length - 1;
              return (
                <tr
                  key={rowIdx}
                  className={getRowClassName(rowIdx)}
                >
                  {row.map((cell, cellIdx) => (
                    <td
                      key={cellIdx}
                      className={`ddb__cell${isTotal ? ' ddb__cell--total' : ''}${cellIdx === 0 ? ' ddb__cell--label' : ''}`}
                    >
                      {cellIdx === 0 ? cell : formatNumber(cell)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default DashboardDataBlock;
